import mapboxgl, { type MapMouseEvent } from "mapbox-gl"
import "mapbox-gl/dist/mapbox-gl.css"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Map, { Layer, Source, type ViewState } from "react-map-gl/mapbox"
import { createFileRoute, useNavigate } from "@tanstack/react-router"

import { Button } from "@fieldbook/ui/button"
import { cn } from "@fieldbook/ui/utils"
import { getGeoJSONFromWkb } from "@fieldbook/common/utils"
import { PanelHeader } from "@/components/project/ProjectDataSheet/common"
import { useGeoLocation } from "@/contexts/GeoLocation"
import { useOfflineMapTiles } from "@/contexts/OfflineMapTilesContext"
import { useProjectData } from "@/contexts/ProjectData"
import {
  estimateMapTileBytes,
  getBoundsFeature,
  getBoundsDimensionsMeters,
  getMapboxSatelliteTileUrl,
  getProjectMapTileBounds,
  getTilesForBounds,
  normalizeBounds,
  type LngLatBounds,
} from "@/lib/offline/mapTiles"

export const Route = createFileRoute(
  "/_private/projects/$id/data/offline/map-tiles",
)({
  component: MapTileCacheRoute,
})

type AreaMode = "project" | "manual"

const ZOOM_PRESETS = [
  { label: "Overview", value: 14 },
  { label: "Detailed", value: 16 },
  { label: "High detail", value: 17 },
] as const

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getBoundsCenter(bounds: LngLatBounds) {
  const [west, south, east, north] = normalizeBounds(bounds)
  return {
    longitude: (west + east) / 2,
    latitude: (south + north) / 2,
  }
}

function getInitialViewState(bounds: LngLatBounds | null): ViewState {
  if (!bounds) {
    return {
      longitude: 117,
      latitude: -30,
      zoom: 5,
      bearing: 0,
      pitch: 0,
      padding: { top: 0, bottom: 0, left: 0, right: 0 },
    }
  }

  const center = getBoundsCenter(bounds)
  return {
    ...center,
    zoom: 12,
    bearing: 0,
    pitch: 0,
    padding: { top: 0, bottom: 0, left: 0, right: 0 },
  }
}

function getLocationViewState(location?: {
  latitude: number
  longitude: number
}): ViewState {
  return {
    longitude: location?.longitude ?? 117,
    latitude: location?.latitude ?? -30,
    zoom: location ? 13 : 5,
    bearing: 0,
    pitch: 0,
    padding: { top: 0, bottom: 0, left: 0, right: 0 },
  }
}

function tileForPoint(longitude: number, latitude: number, zoom: number) {
  return getTilesForBounds(
    [longitude, latitude, longitude, latitude],
    zoom,
    zoom,
  )[0]
}

async function detectMaxAvailableZoom(bounds: LngLatBounds) {
  const center = getBoundsCenter(bounds)
  for (let zoom = 18; zoom >= 12; zoom -= 1) {
    const tile = tileForPoint(center.longitude, center.latitude, zoom)
    if (!tile) continue
    const response = await fetch(getMapboxSatelliteTileUrl(tile), {
      method: "HEAD",
    })
    if (response.ok) return zoom
  }
  return 12
}

function MapTileCacheRoute() {
  if (__FIELDBOOK_TARGET__ !== "capacitor") {
    return <MapTileCacheUnavailable />
  }
  return <MapTileCacheSettings />
}

function MapTileCacheUnavailable() {
  const navigate = useNavigate()
  const { id } = Route.useParams()

  const handleBack = useCallback(() => {
    navigate({ to: "/projects/$id/data", params: { id } })
  }, [id, navigate])

  return (
    <div className="flex flex-col gap-4 pb-8">
      <PanelHeader onBack={handleBack} title="Map tiles" />
      <p className="text-muted-foreground text-sm">
        Offline map tiles are available in the Fieldbook native app. Install the
        native app to download map tiles for use without a connection.
      </p>
    </div>
  )
}

function MapTileCacheSettings() {
  const navigate = useNavigate()
  const { id } = Route.useParams()
  const {
    data: { project },
  } = useProjectData()
  const { location } = useGeoLocation()
  const {
    cachedArea,
    downloadState,
    startDownload,
    cancelDownload,
    removeCachedArea,
  } = useOfflineMapTiles()
  const projectBounds = useMemo(
    () => getProjectMapTileBounds(project),
    [project],
  )
  const [areaMode, setAreaMode] = useState<AreaMode>(
    projectBounds ? "project" : "manual",
  )
  const [manualCorners, setManualCorners] = useState<
    Array<{ lng: number; lat: number }>
  >([])
  const [manualBounds, setManualBounds] = useState<LngLatBounds | null>(null)
  const selectedBounds = areaMode === "project" ? projectBounds : manualBounds
  const [selectedZoom, setSelectedZoom] = useState(16)
  const [detectedMaxZoom, setDetectedMaxZoom] = useState<number | null>(null)
  const [isDetectingMaxZoom, setIsDetectingMaxZoom] = useState(false)
  const [detectMaxZoomError, setDetectMaxZoomError] = useState<string | null>(
    null,
  )
  const [isRemovingCachedArea, setIsRemovingCachedArea] = useState(false)
  const [removeCachedAreaError, setRemoveCachedAreaError] = useState<
    string | null
  >(null)
  const [viewState, setViewState] = useState<ViewState>(() =>
    projectBounds ? getInitialViewState(projectBounds) : getLocationViewState(),
  )
  const hasInitializedProjectBoundsRef = useRef(Boolean(projectBounds))
  const hasInitializedManualLocationRef = useRef(false)

  useEffect(() => {
    if (projectBounds && !hasInitializedProjectBoundsRef.current) {
      hasInitializedProjectBoundsRef.current = true
      setAreaMode("project")
      setViewState(getInitialViewState(projectBounds))
      return
    }

    if (!projectBounds && !hasInitializedManualLocationRef.current) {
      setAreaMode("manual")
      if (location) {
        hasInitializedManualLocationRef.current = true
        setViewState(getLocationViewState(location))
      }
    }
  }, [location, projectBounds])

  const boundsFeature = useMemo(
    () => (selectedBounds ? getBoundsFeature(selectedBounds) : null),
    [selectedBounds],
  )
  const projectBoundaryFeature = useMemo(() => {
    if (!project?.location_multipolygon) return null
    try {
      return getGeoJSONFromWkb<GeoJSON.MultiPolygon>(
        project.location_multipolygon,
      )
    } catch {
      return null
    }
  }, [project?.location_multipolygon])
  const manualCornersFeature = useMemo(
    (): GeoJSON.FeatureCollection<GeoJSON.Point> => ({
      type: "FeatureCollection",
      features: manualCorners.map((corner) => ({
        type: "Feature",
        properties: {},
        geometry: { type: "Point", coordinates: [corner.lng, corner.lat] },
      })),
    }),
    [manualCorners],
  )
  const cachedBoundsFeature = useMemo(
    () => (cachedArea ? getBoundsFeature(cachedArea.bounds) : null),
    [cachedArea],
  )
  const selectedDimensions = useMemo(
    () => (selectedBounds ? getBoundsDimensionsMeters(selectedBounds) : null),
    [selectedBounds],
  )
  const tiles = useMemo(
    () =>
      selectedBounds ? getTilesForBounds(selectedBounds, selectedZoom) : [],
    [selectedBounds, selectedZoom],
  )
  const estimatedBytes = estimateMapTileBytes(tiles.length)
  const maxDetailZoom = detectedMaxZoom ?? 18
  const zoomOptions = useMemo(() => {
    const options = [
      ...ZOOM_PRESETS,
      { label: "Max detail", value: maxDetailZoom },
    ]
    return options.filter(
      (option, index, all) =>
        all.findIndex((other) => other.value === option.value) === index,
    )
  }, [maxDetailZoom])

  const handleBack = useCallback(() => {
    navigate({ to: "/projects/$id/data", params: { id } })
  }, [id, navigate])

  const handleManualMapClick = useCallback(
    (event: MapMouseEvent) => {
      const nextCorners =
        manualCorners.length >= 2
          ? [{ lng: event.lngLat.lng, lat: event.lngLat.lat }]
          : [...manualCorners, { lng: event.lngLat.lng, lat: event.lngLat.lat }]
      setManualCorners(nextCorners)

      if (nextCorners.length === 2) {
        setManualBounds(
          normalizeBounds([
            nextCorners[0].lng,
            nextCorners[0].lat,
            nextCorners[1].lng,
            nextCorners[1].lat,
          ]),
        )
      } else {
        setManualBounds(null)
      }
    },
    [manualCorners],
  )

  const handleDetectMaxZoom = useCallback(async () => {
    if (!selectedBounds) return
    setIsDetectingMaxZoom(true)
    setDetectMaxZoomError(null)
    try {
      const maxZoom = await detectMaxAvailableZoom(selectedBounds)
      setDetectedMaxZoom(maxZoom)
      setSelectedZoom(maxZoom)
    } catch {
      setDetectMaxZoomError(
        "Couldn't check tile availability — are you online?",
      )
    } finally {
      setIsDetectingMaxZoom(false)
    }
  }, [selectedBounds])

  const handleDownload = useCallback(() => {
    if (!selectedBounds || tiles.length === 0) return
    startDownload({ bounds: selectedBounds, zoom: selectedZoom, tiles })
  }, [selectedBounds, selectedZoom, startDownload, tiles])

  const handleClearManualSelection = useCallback(() => {
    setManualCorners([])
    setManualBounds(null)
  }, [])

  const handleRemoveCachedArea = useCallback(async () => {
    setIsRemovingCachedArea(true)
    setRemoveCachedAreaError(null)
    try {
      await removeCachedArea()
    } catch (error) {
      setRemoveCachedAreaError(
        error instanceof Error ? error.message : "Couldn't remove cached area",
      )
    } finally {
      setIsRemovingCachedArea(false)
    }
  }, [removeCachedArea])

  const isDownloading = downloadState.status === "downloading"
  const tileLimitExceeded = tiles.length > 5000

  return (
    <div className="flex flex-col gap-4 pb-8">
      <PanelHeader onBack={handleBack} title="Map tiles" />

      <section className="flex flex-col gap-2">
        <span className="text-muted-foreground text-sm">Area</span>
        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            variant={areaMode === "project" ? "default" : "outline"}
            onClick={() => setAreaMode("project")}
            className="h-auto justify-start py-3"
          >
            Use project area
          </Button>
          <Button
            type="button"
            variant={areaMode === "manual" ? "default" : "outline"}
            onClick={() => setAreaMode("manual")}
            className="h-auto justify-start py-3"
          >
            Manually select area
          </Button>
        </div>
        {areaMode === "project" && !projectBounds && (
          <p className="text-muted-foreground text-sm">
            This project does not have a location polygon or point.
          </p>
        )}
      </section>

      {areaMode === "manual" && (
        <section className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground text-sm">
              Tap two opposite corners
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClearManualSelection}
              disabled={manualCorners.length === 0 && !manualBounds}
            >
              Clear
            </Button>
          </div>
          <div className="h-72 overflow-hidden rounded border">
            <Map
              mapLib={mapboxgl as never}
              mapboxAccessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
              {...viewState}
              onMove={(event) => setViewState(event.viewState)}
              onClick={handleManualMapClick}
              style={{ height: "100%" }}
              mapStyle="mapbox://styles/mapbox/satellite-v9"
            >
              {projectBoundaryFeature && (
                <Source
                  id="project-boundary"
                  type="geojson"
                  data={projectBoundaryFeature}
                >
                  <Layer
                    id="project-boundary-line"
                    type="line"
                    paint={{ "line-color": "#FF00FF", "line-width": 3 }}
                  />
                </Source>
              )}
              {cachedBoundsFeature && (
                <Source
                  id="cached-offline-area"
                  type="geojson"
                  data={cachedBoundsFeature}
                >
                  <Layer
                    id="cached-offline-area-fill"
                    type="fill"
                    paint={{
                      "fill-color": "#16a34a",
                      "fill-opacity": 0.12,
                    }}
                  />
                  <Layer
                    id="cached-offline-area-line"
                    type="line"
                    paint={{
                      "line-color": "#16a34a",
                      "line-dasharray": [2, 1],
                      "line-width": 2,
                    }}
                  />
                </Source>
              )}
              {boundsFeature && (
                <Source
                  id="selected-offline-area"
                  type="geojson"
                  data={boundsFeature}
                >
                  <Layer
                    id="selected-offline-area-fill"
                    type="fill"
                    paint={{
                      "fill-color": "#13ebc3",
                      "fill-opacity": 0.18,
                    }}
                  />
                  <Layer
                    id="selected-offline-area-line"
                    type="line"
                    paint={{ "line-color": "#13ebc3", "line-width": 2 }}
                  />
                </Source>
              )}
              {manualCorners.length > 0 && (
                <Source
                  id="manual-corners"
                  type="geojson"
                  data={manualCornersFeature}
                >
                  <Layer
                    id="manual-corners-point"
                    type="circle"
                    paint={{
                      "circle-radius": 6,
                      "circle-color": "#7f22fe",
                      "circle-stroke-color": "#000000",
                      "circle-stroke-width": 1,
                    }}
                  />
                </Source>
              )}
            </Map>
          </div>
          {selectedDimensions && (
            <p className="text-muted-foreground text-sm">
              {Math.round(selectedDimensions.widthMeters).toLocaleString()} m x{" "}
              {Math.round(selectedDimensions.heightMeters).toLocaleString()} m
            </p>
          )}
        </section>
      )}

      {areaMode === "project" && selectedDimensions && (
        <p className="text-muted-foreground text-sm">
          Project area:{" "}
          {Math.round(selectedDimensions.widthMeters).toLocaleString()} m x{" "}
          {Math.round(selectedDimensions.heightMeters).toLocaleString()} m
        </p>
      )}

      {cachedArea && (
        <section className="flex flex-col gap-2 border-y py-3">
          <span className="text-sm font-medium">Currently cached area</span>
          <span className="text-muted-foreground text-sm">
            z{cachedArea.zoom}, {cachedArea.tileCount.toLocaleString()} tiles,{" "}
            {formatBytes(cachedArea.byteSize)}
          </span>
          {!isDownloading && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRemoveCachedArea}
              disabled={isRemovingCachedArea}
              className="text-destructive hover:text-destructive w-fit"
            >
              {isRemovingCachedArea ? "Removing…" : "Remove cached area"}
            </Button>
          )}
          {removeCachedAreaError && (
            <p className="text-destructive text-sm">{removeCachedAreaError}</p>
          )}
        </section>
      )}

      <section className="flex flex-col gap-2">
        <span className="text-muted-foreground text-sm">Zoom level</span>
        <div className="grid grid-cols-2 gap-2">
          {zoomOptions.map((option) => (
            <Button
              key={option.label}
              type="button"
              variant={selectedZoom === option.value ? "default" : "outline"}
              onClick={() => setSelectedZoom(option.value)}
              className="h-auto justify-start py-3"
            >
              {option.label}
            </Button>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleDetectMaxZoom}
          disabled={!selectedBounds || isDetectingMaxZoom}
        >
          {isDetectingMaxZoom ? "Checking…" : "Find max detail"}
        </Button>
        {detectedMaxZoom && (
          <p className="text-muted-foreground text-sm">
            Max detail available around this area: z{detectedMaxZoom}
          </p>
        )}
        {detectMaxZoomError && (
          <p className="text-destructive text-sm">{detectMaxZoomError}</p>
        )}
      </section>

      <section className="flex flex-col gap-2 border-y py-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Tiles</span>
          <span>
            {selectedBounds ? tiles.length.toLocaleString() : "No area"}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Estimated size</span>
          <span>
            {selectedBounds ? formatBytes(estimatedBytes) : "No area"}
          </span>
        </div>
        {tileLimitExceeded && (
          <p className="text-destructive text-sm">
            This area is large. Reduce the area or choose a lower zoom level.
          </p>
        )}
      </section>

      {downloadState.status === "downloading" && (
        <section className="flex flex-col gap-2">
          <div className="bg-muted h-2 overflow-hidden rounded">
            <div
              className="h-full bg-green-600 transition-all"
              style={{
                width: `${((downloadState.completed + downloadState.skipped) / downloadState.total) * 100}%`,
              }}
            />
          </div>
          <p className="text-muted-foreground text-sm">
            {downloadState.completed} of {downloadState.total} tiles,{" "}
            {formatBytes(downloadState.bytes)}
            {downloadState.skipped > 0
              ? ` (${downloadState.skipped} tiles unavailable at this zoom)`
              : ""}
          </p>
        </section>
      )}

      {downloadState.status === "complete" && (
        <p className="text-sm text-green-600">
          Cached {downloadState.total - downloadState.skipped} tiles,{" "}
          {formatBytes(downloadState.bytes)}.
          {downloadState.cached > 0
            ? ` ${downloadState.cached} already cached.`
            : ""}
          {downloadState.skipped > 0
            ? ` ${downloadState.skipped} tiles unavailable at this zoom.`
            : ""}
        </p>
      )}
      {downloadState.status === "cancelled" && (
        <p className="text-muted-foreground text-sm">
          Cancelled after {downloadState.completed} of {downloadState.total}{" "}
          tiles.
          {downloadState.skipped > 0
            ? ` ${downloadState.skipped} tiles unavailable at this zoom.`
            : ""}
        </p>
      )}
      {downloadState.status === "error" && (
        <p className="text-destructive text-sm">
          Failed after {downloadState.completed} of {downloadState.total} tiles:{" "}
          {downloadState.message}
          {downloadState.skipped > 0
            ? ` (${downloadState.skipped} tiles unavailable at this zoom)`
            : ""}
        </p>
      )}

      <div className="flex gap-2">
        <Button
          type="button"
          onClick={handleDownload}
          disabled={
            !selectedBounds ||
            tiles.length === 0 ||
            tileLimitExceeded ||
            isDownloading
          }
          className={cn("flex-1", isDownloading && "opacity-80")}
        >
          {isDownloading ? "Caching…" : "Cache map tiles"}
        </Button>
        {isDownloading && (
          <Button type="button" variant="outline" onClick={cancelDownload}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  )
}
